export function removeTrailingZeros(str: string, minTargetLength = 0): string {
  let result = str;
  while (result.endsWith('0') && result.length > minTargetLength) {
    result = result.slice(0, -1);
  }
  return result;
}

export function isValidEmail(email: string): boolean {
  const emailRegex =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
}

export function extractDomainFromEmail(email: string) {
  const domain = email.split('@')[1];
  const parts = domain.split('.');
  if (parts.length <= 2) {
    return domain;
  }
  return parts.slice(-2).join('.');
}

export function removeEmoji(string: string) {
  const regexEmoji =
    /(\u00a9|\u00ae|[\u2000-\u3300]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff])/gi;
  return string.replace(regexEmoji, '');
}

export function encodeObjectToBase64<T extends object>(obj: T): string {
  // throw if object is empty
  if (Object.keys(obj).length === 0) {
    throw new Error('Object is empty');
  }
  const locationData = JSON.stringify(obj);
  // using TextEncoder to encode strings containing characters outside of the ASCII range
  const utf8Array = new TextEncoder().encode(locationData);
  const base64String = btoa(String.fromCharCode(...utf8Array));
  const urlSafeEncodedData = encodeURIComponent(base64String);
  return urlSafeEncodedData;
}

export function decodeObjectFromBase64<T extends object>(
  encodedData: string,
): T | null {
  if (!encodedData.length) {
    return null;
  }
  const urlSafeEncodedData = decodeURIComponent(encodedData);

  try {
    const decodedData = atob(urlSafeEncodedData);
    const utf8Array = new Uint8Array(
      decodedData.split('').map(char => char.charCodeAt(0)),
    );
    const decodedString = new TextDecoder().decode(utf8Array);

    return JSON.parse(decodedString) as T;
  } catch (e) {
    return null;
  }
}

export function normalizeString(string: string) {
  return string
    .normalize('NFD') // Normalize to decomposed form (letters are separate from accents)
    .replace(/[\u0300-\u036f]/g, '') // Remove accents
    .toLowerCase();
}

export function capitalizeFirstLetter(string: string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}
/**
 * Compares two strings in a way that handles numbers naturally.
 * This is useful for sorting strings containing numbers in a human-friendly order.
 * ie. ['1', '2', '10'] instead of ['1', '10', '2']
 *
 * @param {string} a - The first string to compare.
 * @param {string} b - The second string to compare.
 * @returns {number} - Returns -1 if `a` < `b`, 1 if `a` > `b`, and 0 if `a` === `b`.
 */
export function naturalCompare(a: string, b: string): number {
  if (a === '' && b === '') return 0;
  if (a === '') return -1;
  if (b === '') return 1;

  const aParts = a
    .split(/(\d+)/)
    .map(part => (isNaN(Number(part)) ? part : Number(part)));
  const bParts = b
    .split(/(\d+)/)
    .map(part => (isNaN(Number(part)) ? part : Number(part)));

  for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
    if (aParts[i] === undefined) return -1;
    if (bParts[i] === undefined) return 1;
    if (aParts[i] < bParts[i]) return -1;
    if (aParts[i] > bParts[i]) return 1;
  }

  return 0;
}

export function encodeFilePathWithEncodedParenthesis(filePath: string): string {
  if (!filePath) {
    return '';
  }
  return filePath
    .split('/')
    .map(segment =>
      encodeURIComponent(segment).replace(/\(/g, '%28').replace(/\)/g, '%29'),
    )
    .join('/');
}

/**
 * Creates accent-insensitive variations of a query string for highlighting purposes.
 * This function generates all possible accent variations of each character in the query.
 *
 * @param {string} query - The original query string
 * @returns {string[]} - Array of all possible accent variations of the query
 */
export function createAccentVariations(query: string): string[] {
  const accentMap: Record<string, string[]> = {
    a: ['a', 'à', 'á', 'â', 'ã', 'ä', 'å', 'ā', 'ă', 'ą'],
    e: ['e', 'è', 'é', 'ê', 'ë', 'ē', 'ĕ', 'ė', 'ę'],
    i: ['i', 'ì', 'í', 'î', 'ï', 'ī', 'ĭ', 'į'],
    o: ['o', 'ò', 'ó', 'ô', 'õ', 'ö', 'ō', 'ŏ', 'ő'],
    u: ['u', 'ù', 'ú', 'û', 'ü', 'ū', 'ŭ', 'ů', 'ű'],
    y: ['y', 'ý', 'ÿ', 'ŷ'],
    c: ['c', 'ç', 'ć', 'ĉ', 'ċ', 'č'],
    n: ['n', 'ñ', 'ń', 'ņ', 'ň'],
    s: ['s', 'ś', 'ŝ', 'ş', 'š'],
    z: ['z', 'ź', 'ż', 'ž'],
    A: ['A', 'À', 'Á', 'Â', 'Ã', 'Ä', 'Å', 'Ā', 'Ă', 'Ą'],
    E: ['E', 'È', 'É', 'Ê', 'Ë', 'Ē', 'Ĕ', 'Ė', 'Ę'],
    I: ['I', 'Ì', 'Í', 'Î', 'Ï', 'Ī', 'Ĭ', 'Į'],
    O: ['O', 'Ò', 'Ó', 'Ô', 'Õ', 'Ö', 'Ō', 'Ŏ', 'Ő'],
    U: ['U', 'Ù', 'Ú', 'Û', 'Ü', 'Ū', 'Ŭ', 'Ů', 'Ű'],
    Y: ['Y', 'Ý', 'Ÿ', 'Ŷ'],
    C: ['C', 'Ç', 'Ć', 'Ĉ', 'Ċ', 'Č'],
    N: ['N', 'Ñ', 'Ń', 'Ņ', 'Ň'],
    S: ['S', 'Ś', 'Ŝ', 'Ş', 'Š'],
    Z: ['Z', 'Ź', 'Ż', 'Ž'],
  };

  // Create reverse mapping from accented characters to their base character
  const reverseAccentMap: Record<string, string> = {};
  Object.entries(accentMap).forEach(([baseChar, accentedChars]) => {
    accentedChars.forEach(accentedChar => {
      reverseAccentMap[accentedChar] = baseChar;
    });
  });

  const variations = new Set<string>();
  variations.add(query); // Add original query

  // Generate variations by replacing each character with its accent variations
  for (let i = 0; i < query.length; i++) {
    const char = query[i];

    // Check if the character is in the accent map (base character)
    let accentVariations = accentMap[char];

    // If not found, check if it's an accented character and get its base character
    if (!accentVariations) {
      const baseChar = reverseAccentMap[char];
      if (baseChar) {
        accentVariations = accentMap[baseChar];
      }
    }

    if (accentVariations) {
      accentVariations.forEach(accentChar => {
        const variation = query.slice(0, i) + accentChar + query.slice(i + 1);
        variations.add(variation);
      });
    }
  }

  return Array.from(variations);
}
